import React, { useState } from 'react';
import ChangeLogForm from '../components/ChangeLogForm';
import '../ChangeLog.css'; // Import standard CSS

const STATIC_CHANGE_LOGS = [
    {
        id: "1.0.8",
        version: "Version 1.0.8",
        release_date: "2026-06-15",
        changes: [
            {
                id: "c1",
                type: "Update",
                title: "Role-Based Access & Department Admin Updates",
                items: [
                    "Improved role-based access for Global Admin, Operational Admin, Department Admin, and User.",
                    "Department Admin can view all team tasks under their department.",
                    "Added support for users belonging to multiple departments/teams.",
                    "Improved task visibility based on department/team membership."
                ]
            }
        ]
    },
    {
        id: "1.0.7",
        version: "Version 1.0.7",
        release_date: "2026-06-10",
        changes: [
            {
                id: "c2",
                type: "Update",
                title: "Header, Sidebar & UI Improvements",
                items: [
                    "Updated application header with AGS TASK PLANNER title.",
                    "Improved sidebar navigation layout.",
                    "Added New Task navigation item.",
                    "Fixed logo size and alignment.",
                    "Improved profile panel design.",
                    "Fixed theme toggle and dropdown UI issues."
                ]
            }
        ]
    },
    {
        id: "1.0.6",
        version: "Version 1.0.6",
        release_date: "2026-06-05",
        changes: [
            {
                id: "c3",
                type: "Update",
                title: "Calendar & Mobile View Improvements",
                items: [
                    "Improved calendar task display.",
                    "Added mobile responsive calendar layout.",
                    "Made each day display tasks in a cleaner mobile card format.",
                    "Adjusted calendar spacing, task cards, and header alignment for mobile view."
                ]
            }
        ]
    },
    {
        id: "1.0.5",
        version: "Version 1.0.5",
        release_date: "2026-06-01",
        changes: [
            {
                id: "c4",
                type: "New",
                title: "Loop Task & Schedule Improvements",
                items: [
                    "Added loop task scheduling with daily, weekly, monthly, and yearly repeat options.",
                    "Added preview schedule feature.",
                    "Added loop duration fields.",
                    "Improved calendar preview for recurring tasks.",
                    "Fixed loop task regeneration issue where old loop tasks were being recreated."
                ]
            }
        ]
    },
    {
        id: "1.0.4",
        version: "Version 1.0.4",
        release_date: "2026-05-25",
        changes: [
            {
                id: "c5",
                type: "Update",
                title: "File Management Updates",
                items: [
                    "Unified files from task form, issues, user cases, and extension requests.",
                    "Added file filters for images, documents, and sheets.",
                    "Enabled file preview/open from the Files tab.",
                    "Improved attachment handling with Supabase Storage.",
                    "Fixed issue where uploaded files disappeared after a short time."
                ]
            }
        ]
    },
    {
        id: "1.0.3",
        version: "Version 1.0.3",
        release_date: "2026-05-20",
        changes: [
            {
                id: "c6",
                type: "Update",
                title: "Task Details Page Enhancements",
                items: [
                    "Added Overview, Files, Request Extension, Issues, and User Cases tabs.",
                    "Added ability to add/remove assignees from TaskDetailsPage.",
                    "Added issue tracking, user cases, notes, and extension request workflow.",
                    "Fixed incorrect requested by name in extension requests.",
                    "Fixed task date display issue showing one day backward."
                ]
            }
        ]
    },
    {
        id: "1.0.2",
        version: "Version 1.0.2",
        release_date: "2026-05-15",
        changes: [
            {
                id: "c7",
                type: "New",
                title: "Notifications & Email Improvements",
                items: [
                    "Added task creation notifications and email alerts for assigned users.",
                    "Fixed notification/email issue where assigner was receiving alerts incorrectly.",
                    "Added notifications and emails for loop task creation.",
                    "Improved due date reminder email flow.",
                    "Added extension request email support for department admins."
                ]
            }
        ]
    },
    {
        id: "1.0.1",
        version: "Version 1.0.1",
        release_date: "2026-05-10",
        changes: [
            {
                id: "c8",
                type: "New",
                title: "Task Assignment & Multi-Assignee Improvements",
                items: [
                    "Added support for assigning one task to multiple users.",
                    "Updated task assignment logic to use task_assignees table.",
                    "Improved visibility so assigned users can see their tasks correctly.",
                    "Fixed issue where unassigned tasks appeared in calendar.",
                    "Calendar task click now opens TaskDetailsPage instead of TaskForm."
                ]
            }
        ]
    }
];

const ChangeLog = () => {
    const changeLogs = STATIC_CHANGE_LOGS;
    const [isFormOpen, setIsFormOpen] = useState(false);

    const getTypeBadgeClass = (type) => {
        switch (type) {
            case 'New': return 'badge badge-new';
            case 'Update': return 'badge badge-update';
            case 'Bugfix': return 'badge badge-bugfix';
            default: return 'badge';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <div className="change-log-container" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
            <div className="change-log-header" style={{ marginBottom: '32px' }}>
                <h1 className="change-log-title" style={{ fontSize: '32px', fontWeight: 'bold' }}>Change Logs</h1>
            </div>

            {changeLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    <p className="text-xl">No release notes found.</p>
                </div>
            ) : (
                <div className="change-log-list" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {changeLogs.map((log) => (
                        <div key={log.id} className="release-entry card" style={{ 
                            padding: '24px', 
                            borderRadius: '12px', 
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            border: '1px solid var(--border-color)',
                            backgroundColor: 'var(--card-bg)'
                        }}>
                            <div className="release-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                                <h2 className="release-version" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>{log.version}</h2>
                                <span className="release-date" style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{formatDate(log.release_date)}</span>
                            </div>

                            <div className="release-changes" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {log.changes.map((change) => (
                                    <div key={change.id} className="change-card">
                                        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span className={getTypeBadgeClass(change.type)} style={{ 
                                                padding: '4px 10px', 
                                                borderRadius: '16px', 
                                                fontSize: '12px', 
                                                fontWeight: 'bold',
                                                backgroundColor: change.type === 'New' ? '#dcfce7' : '#eff6ff',
                                                color: change.type === 'New' ? '#166534' : '#1e40af'
                                            }}>
                                                {change.type}
                                            </span>
                                            <h3 className="change-title" style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                                {change.title}
                                            </h3>
                                        </div>

                                        <ul className="change-list" style={{ margin: '8px 0 0 0', paddingLeft: '24px', color: 'var(--text-secondary)' }}>
                                            {change.items && Array.isArray(change.items) && change.items.map((item, i) => (
                                                <li key={i} className="change-item" style={{ marginBottom: '6px', lineHeight: '1.5' }}>
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isFormOpen && (
                <ChangeLogForm
                    onClose={() => setIsFormOpen(false)}
                    onSave={() => setIsFormOpen(false)}
                />
            )}
        </div>
    );
};

export default ChangeLog;
